import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';

export const ContactPage = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast({
      title: t('success'),
      description: t('contactSuccess'),
    });
  };

  const seoTitle = language === 'fr' 
    ? "Contact | Gaius - Formation et Audit IA Juridique"
    : "Contact | Gaius - Legal AI Training and Audit";
    
  const seoDescription = language === 'fr'
    ? "Contactez Gaius pour vos besoins en formation IA ou audit de stratégie IA. Basé à Paris, disponible en Europe et à l'international."
    : "Contact Gaius for your AI training or AI strategy audit needs. Based in Paris, available across Europe and internationally.";

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={language === 'fr' ? "contact gaius, formation ia avocat paris, audit ia cabinet" : "contact gaius, legal ai training paris, law firm ai audit"}
        canonicalUrl="https://gaius-tech.com/contact"
        alternateUrlFr="https://gaius-tech.com/contact"
        alternateUrlEn="https://gaius-tech.com/contact"
      />
      
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div className="max-w-3xl">
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                {t('contactTitle')}
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                {t('contactSubtitle')}
              </p>
            </div>
            <div className="mt-12 lg:mt-0">
              <img 
                src="/images/seo-contact-formation-ia-cabinet.webp"
                alt={language === 'fr'
                  ? "Contact Gaius - Formation IA pour cabinets d'avocats, intelligence artificielle juridique, CNB, avocat augmenté, direction juridique"
                  : "Contact Gaius - AI training for law firms, legal artificial intelligence, bar council, augmented lawyer, legal department"}
                title={language === 'fr'
                  ? "Contactez Gaius pour votre formation intelligence artificielle juridique - Avocats, juristes, directions juridiques"
                  : "Contact Gaius for your legal AI training - Lawyers, counsel, legal departments"}
                loading="lazy"
                width={1200}
                height={630}
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16">
            {/* Contact Form */}
            <div>
              <Card className="bg-white border-border">
                <CardHeader>
                  <CardTitle className="text-2xl text-foreground">
                    {language === 'fr' ? 'Envoyez-nous un message' : 'Send us a message'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' 
                      ? 'Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.'
                      : 'Fill out the form below and we\'ll get back to you soon.'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        {language === 'fr' ? 'Message envoyé !' : 'Message sent!'}
                      </h3>
                      <p className="text-muted-foreground">
                        {t('contactSuccess')}
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">{t('contactName')}</Label>
                          <Input 
                            id="name" 
                            name="name"
                            required 
                            placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">{t('contactEmail')}</Label>
                          <Input 
                            id="email" 
                            name="email"
                            type="email" 
                            required 
                            placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="company">{t('contactCompany')}</Label>
                        <Input 
                          id="company" 
                          name="company"
                          placeholder={language === 'fr' ? 'Nom du cabinet ou entreprise' : 'Firm or company name'}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="subject">{t('contactSubject')}</Label>
                        <Input 
                          id="subject" 
                          name="subject"
                          required 
                          placeholder={language === 'fr' ? 'Objet de votre message' : 'Subject of your message'}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="message">{t('contactMessage')}</Label>
                        <Textarea 
                          id="message" 
                          name="message"
                          required 
                          rows={5}
                          placeholder={language === 'fr' ? 'Décrivez votre projet ou vos questions...' : 'Describe your project or questions...'}
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full bg-primary hover:bg-primary/90"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span>{t('loading')}</span>
                        ) : (
                          <>
                            <Send className="mr-2 w-4 h-4" />
                            {t('contactSend')}
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="mt-12 lg:mt-0">
              <h2 className="text-2xl font-bold text-foreground mb-8">
                {t('contactInfo')}
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Email</h3>
                    <a 
                      href="mailto:contact@gaius-tech.com" 
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      contact@gaius-tech.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {language === 'fr' ? 'Localisation' : 'Location'}
                    </h3>
                    <p className="text-muted-foreground">{t('contactLocation')}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('contactAvailability')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional info card */}
              <Card className="mt-12 bg-secondary/30 border-border">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-foreground mb-4">
                    {language === 'fr' ? 'Notre équipe' : 'Our Team'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr'
                      ? 'Une équipe pluridisciplinaire d\'avocats et d\'experts tech, combinant expertise juridique et maîtrise des technologies d\'entreprise pour vous accompagner dans votre transformation IA.'
                      : 'A multidisciplinary team of lawyers and tech experts, combining legal expertise with enterprise technology mastery to support your AI transformation.'
                    }
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
